import React, {useEffect, useState, useMemo} from "react";
import {ACard} from "@atiautomacao/ati-ui-library";
import { Autocomplete, Box, Button, Checkbox, Grid, IconButton, InputBase, InputLabel, TextField, Collapse, useTheme, Divider, Typography, Drawer} from "@mui/material";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { TeleObjectMapCurrentsReactQuery, TeleObjectMapTrackerReactQuery, TeleObjectMapSaveReactQuery, ConfigurationByNameReactQuery } from "../../PowerStationReactQuery";
import { useSnackbar } from 'notistack';
import { LoadingButton } from "@mui/lab";

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 4,
};

interface ParamsModalProps {
    open: boolean;
    onCloseModal: any;
    data?: any;
    onSave?:any;
    dataForm?:any;
    id?: any;
    selectedTeleObjectMapEdit?: any;
    teleObjectMapIdsExist?: number[];
}

interface TeleObject {
teleObjectId: number;
teleObjectConfigName: string;
teleObjectConfigDataTypeReference?: number;
equipmentId?: number;
equipmentName?: string;
equipmentTypeId?: number;
count?: number;
skidId?: number;
skidName?: string;
teleObjectconcatenatedName?: string;
}

interface GroupedResponse {
data: Record<string, TeleObject[]>;
}

let MAX_ROWS = 40;
const TELEOBJECT_MAP_MAX_NUMBER = "teleobjectmapmaxnumber";


export const SideBar = ({open, onCloseModal, data, onSave, id, selectedTeleObjectMapEdit, teleObjectMapIdsExist}: ParamsModalProps) => {
    const theme = useTheme();
    const [trackers, setTrackers] = useState<any>([]);
    const [currents, setCurrents] = useState<GroupedResponse>({data: {}});
    const [selectedTracker, setSelectedTracker] = useState<any>(null);
    const [selectedTeleObjectId, setSelectedTeleObjectId] = useState<number | null>(selectedTeleObjectMapEdit?.teleObjectMapItemList?.[0]?.teleObjectFrom?.id ?? null);
    const [loading, setLoading] = useState<boolean>(false);
    const findMutateTrackers = TeleObjectMapTrackerReactQuery();
    const findMutateCurrents = TeleObjectMapCurrentsReactQuery();
    const configurationByName = ConfigurationByNameReactQuery(TELEOBJECT_MAP_MAX_NUMBER);
    
    const saveMutate = TeleObjectMapSaveReactQuery(id);
    const { enqueueSnackbar } = useSnackbar();
    
    const [selectedCurrents, setSelectedCurrents] = useState<Record<string, number[]>>({});
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        if(configurationByName.isSuccess && configurationByName.data.data.value) {
            MAX_ROWS = configurationByName.data.data.value;
        }
    }, [configurationByName.isSuccess]);
    
    useEffect(() => {
        if (open) {
            findMutateTrackers.mutate(id, {
                onSuccess: (data: any) => {
                    setTrackers(data.data)
                }
            })
            findMutateCurrents.mutate(id, {
                onSuccess: (data: any) => {

                    setCurrents({ data: data.data });

                    if (selectedTeleObjectMapEdit?.teleObjectMapItemList?.length) {
                        const initializeSelectedCheckboxes: Record<string, number[]> = {};
                        const grouped: Record<string, TeleObject[]> = data.data || {};

                        selectedTeleObjectMapEdit.teleObjectMapItemList.forEach((mi: any) => {
                            const curId = mi?.teleObjectTo?.id;
                            if (curId == null) return;
                            const found = Object.entries(grouped).find(([groupName, items]) =>
                                (items || []).some((i: TeleObject) => i.teleObjectId === curId)
                            );
                            if (found) {
                                const groupName = found[0];
                                initializeSelectedCheckboxes[groupName] = initializeSelectedCheckboxes[groupName] ?? [];
                                if (!initializeSelectedCheckboxes[groupName].includes(curId)) {
                                    initializeSelectedCheckboxes[groupName].push(curId);
                                }
                            }
                        });

                        if (Object.keys(initializeSelectedCheckboxes).length > 0) {
                            setSelectedCurrents(initializeSelectedCheckboxes);
                            setExpandedGroups(prev => ({ ...prev, ...Object.fromEntries(Object.keys(initializeSelectedCheckboxes).map(k => [k, true])) }));
                        }
                    }
                }
            })
        }
     }, [open, id]);   

    const getChildIds = (groupName: string) => {
        return currents?.data?.[groupName]?.map((c: TeleObject) => c.teleObjectId) ?? [];
    }

    const toggleParent = (groupName: string) => {
        const childIds = getChildIds(groupName);
        const selected = selectedCurrents[groupName] ?? [];        
        
        if (selected.length === childIds.length && childIds.length > 0) {
            setSelectedCurrents(prev => {
                const next = { ...prev };
                delete next[groupName];
                return next;
            });
        } else {
            setSelectedCurrents(prev => ({ ...prev, [groupName]: [...childIds] }));
        }
    }

    const toggleChild = (groupName: string, id: number) => {
        setSelectedCurrents(prev => {
            const prevSet = new Set(prev[groupName] ?? []);
            if (prevSet.has(id)) prevSet.delete(id);
            else prevSet.add(id);
            return { ...prev, [groupName]: Array.from(prevSet) };
        });
    }

    const toggleGroup = (groupName: string) => {
        setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    }

    useEffect(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return;
        const matchingGroups = Object.entries(currents.data || {}).filter(([_, items]) =>
            items.some(i => (i.teleObjectConfigName ?? '').toLowerCase().includes(q))
        ).map(([k]) => k);
        if (matchingGroups.length > 0) {
            setExpandedGroups(prev => {
                const next = { ...prev };
                matchingGroups.forEach(g => next[g] = true);
                return next;
            });
        }
    }, [searchQuery, currents.data]);

    const filteredCurrents = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return Object.entries(currents.data || {}).reduce((acc: Record<string, TeleObject[]>, [k, items]) => {
            if (!q) { acc[k] = items; return acc; }
            const matches = items.filter(i => (i.teleObjectConfigName ?? '').toLowerCase().includes(q));
            if (matches.length) acc[k] = matches;
            return acc;
        }, {});
    }, [searchQuery, currents.data]);

    useEffect(() => {
            if (selectedTeleObjectMapEdit) {
                setSelectedTeleObjectId(selectedTeleObjectMapEdit?.teleObjectMapItemList?.[0]?.teleObjectFrom?.id ?? null);
            }
     }, [selectedTeleObjectMapEdit]);

    useEffect(() => {
            if (!open) {
                setSelectedTracker(null);
                setSelectedTeleObjectId(null);
                setSelectedCurrents({});
                setExpandedGroups({});
                setSearchQuery('');
            }
     }, [onCloseModal]);

     console.log("loading",loading);
     
    const onSubmit = () => {
        if (!selectedTeleObjectId) {
            enqueueSnackbar('Selecione um tracker antes de salvar.', { variant: 'warning' });
            return;
        }

        const countSelectedCurrents = Object.values(selectedCurrents).reduce((acc, ids) => acc + (ids?.length ?? 0), 0);
        if (countSelectedCurrents > MAX_ROWS) {
            enqueueSnackbar(`Número máximo de correntes mapeadas excedido. Máximo permitido: ${MAX_ROWS}`, { variant: 'warning' });
            return;
        }
        if (countSelectedCurrents === 0) {
            enqueueSnackbar('Selecione ao menos uma corrente para mapear.', { variant: 'warning' });
            return;
        }

        setLoading(true);

        const findCurrentById = (id: number) => {
            const lists = Object.values(currents.data || {});
            for (const arr of lists) {
                const found = arr.find((i: TeleObject) => i.teleObjectId === id);
                if (found) return found;
            }
            return null;
        }
        const findTrackerById = (id: number) => {
            return trackers?.find((t: any) => t.teleObjectId === id) ?? null;
        }

    

        const teleObjectMapItemList: any[] = [];
        const fromItem = findTrackerById(selectedTeleObjectId);
        Object.values(selectedCurrents).forEach((ids: number[]) => {
            (ids || []).forEach((curId: number) => {
                const foundItem = findCurrentById(curId);
                const mapItemByCurId = selectedTeleObjectMapEdit?.teleObjectMapItemList?.find((mi: any) => mi?.teleObjectTo?.id === curId);
                teleObjectMapItemList.push({
                    id: mapItemByCurId?.id ?? null,
                    name: mapItemByCurId?.name?.length > 0 ? mapItemByCurId.name : null,
                    teleObjectFrom: {
                        id: selectedTeleObjectId,
                        teleObjectConfig: {
                            name: fromItem?.teleObjectConfigName ?? null,
                        },
                        equipment: {
                            id: fromItem?.equipmentId ?? null,
                            name: fromItem?.equipmentName ?? null,
                            skid: {
                                id: fromItem?.skidId ?? null,
                                name: fromItem?.skidName ?? null
                            }
                        }
                    },
                    teleObjectTo: {
                        id: curId,
                        teleObjectConfig: {
                            name: foundItem?.teleObjectConfigName ?? null,
                        },
                        equipment: {
                            id: foundItem?.equipmentId ?? null,
                            name: foundItem?.equipmentName ?? null,
                            skid: {
                                id: foundItem?.skidId ?? null,
                                name: foundItem?.skidName ?? null
                            }
                        }
                    },
                    teleObjectMap: {
                        id: selectedTeleObjectMapEdit?.id ?? null,
                    }
                });
            });
        });

        if (teleObjectMapItemList.length === 0) {
            enqueueSnackbar('Selecione ao menos uma corrente para mapear.', { variant: 'warning' });
            return;
        }

        const payload = {
            id: selectedTeleObjectMapEdit?.id ?? null,
            name: null,
            mappingType: '0',
            teleObjectMapItemList,
            createdAt: new Date().toISOString()
        };
        
        saveMutate.mutate(payload, {
            onSuccess: (response: any) => {
                if (onSave){
                    onSave(response)
                };
                setLoading(false)                    
                onCloseModal();
            },
            onError: (error: any) => {
                setLoading(false)
            }
        });
    }   
    
    const isTrackerInAnotherMap = useMemo(() => {
        return selectedTracker?.teleObjectId && teleObjectMapIdsExist?.includes(selectedTracker.teleObjectId);
    }, [selectedTracker, teleObjectMapIdsExist]);
     
    return (
        <Drawer open={open} anchor="right"
            PaperProps={{sx: {width: '418px', mt: '60px', backgroundColor: theme.palette.background.default}}}
            onClose={onCloseModal} aria-labelledby="drawer-sidebar-title" 
            aria-describedby="modal-modal-description"
        >
                <ACard
                    tooltipTitle=""
                    title={selectedTeleObjectMapEdit ? "Editar Mapeamento" : "Novo Mapeamento"}
                    subTitle={<Divider /> as unknown as string}
                    styleProps={{
                        cardStyle: {padding: 0},
                        headerStyle: {padding: '16px 24px 0px 24px'},
                        subTitleStyle: {paddingTop: '0px'}
                    }}
                    footerActions={
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 2, mb: 2, padding: '0 24px' }}>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                loading={loading}
                                onClick={onSubmit}
                                disabled={loading || isTrackerInAnotherMap}
                            >
                                Finalizar Edição
                            </LoadingButton>
                            <LoadingButton variant="outlined" color="secondary" onClick={onCloseModal} loading={loading}>
                                Cancelar
                            </LoadingButton>
                        </Box>
                    }
                >
                    <Grid container spacing={3} sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px', paddingTop: 0 }}>
                        <Grid item xs={12}>
                            <InputLabel id="tracker-label">Selecione o tracker</InputLabel>
                            <Autocomplete
                                disablePortal
                                options={trackers || []}
                                disabled={selectedTeleObjectMapEdit?.id != null}
                                getOptionLabel={(option: any) => option?.teleObjectConcatenatedName ?? ''}
                                isOptionEqualToValue={(option: any, value: any) => option?.teleObjectId === value?.teleObjectId}
                                value={selectedTracker ?? trackers?.find((tracker: any) => tracker.teleObjectId === selectedTeleObjectMapEdit?.teleObjectMapItemList?.[0]?.teleObjectFrom?.id) ?? { teleObjectConcatenatedName: "Busca por tracker..." }}
                                onChange={(event: any, value: any) => {
                                    setSelectedTracker(value);
                                    setSelectedTeleObjectId(value?.teleObjectId ?? null);
                                }}
                                sx={{ width: '100%', height: '40px', marginBottom: isTrackerInAnotherMap ? 4 : 0}}
                                renderInput={(params) => <TextField error={isTrackerInAnotherMap}  {...params}
                                     helperText={isTrackerInAnotherMap ? "O tracker selecionado já está mapeado em outro mapeamento." : ""} />}
                                
                            />
                        </Grid>
                        <Grid item xs={12} >
                            <InputLabel id="tracker-label">Selecione as correntes</InputLabel>
                            <Box sx={{ height: '40px',display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: "4px", padding: "4px" }}>
                                
                            <InputBase
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ ml: 1, flex: 1, width: '90%' }}
                                placeholder="Buscar"
                                inputProps={{ 'aria-label': 'Buscar' }}
                            />
                            <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
                                <SearchIcon />
                            </IconButton>

                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            {Object.keys(filteredCurrents).length === 0 ? (
                                <div>Nenhuma corrente encontrada.</div>
                            ) : (
                                Object.entries(filteredCurrents).map(([groupName, items]) => {
                                    const childIds = getChildIds(groupName);
                                    
                                    
                                    const selected = selectedCurrents[groupName] ?? [];
                                    const parentChecked = childIds.length > 0 && selected.length === childIds.length;
                                    const parentIndeterminate = selected.length > 0 && selected.length < childIds.length;
                                    return (
                                        <Box key={groupName} sx={{ mb:2, mt:1, alignItems: 'center', backgroundColor: '#FAFBFF', borderRadius: '4px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: '4px' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{groupName}</Typography>
                                                <IconButton size="small" sx={{ color: theme.palette.primary.main }} onClick={() => toggleGroup(groupName)}>
                                                    {expandedGroups[groupName] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Box>
                                            <Collapse in={!!expandedGroups[groupName]} timeout="auto" unmountOnExit>
                                                <Box sx={{ px: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: '4px' }}>
                                                        <Typography variant="body2" sx={{ mr: 2, color: 'text.secondary' }}>Selecionar todas</Typography>
                                                        <Checkbox
                                                            checked={parentChecked}
                                                            indeterminate={parentIndeterminate}
                                                            onChange={() => toggleParent(groupName)}
                                                        />
                                                    </Box>
                                                    <Divider />
                                                    <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                                                        {items.map((item: TeleObject) => (
                                                            <Box key={item.teleObjectId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '4px' }}>
                                                                <Box>
                                                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{item.teleObjectConfigName ?? `#${item.teleObjectId}`}</Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {item.count && item.count > 0 ? (
                                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{`Em ${item.count}/${trackers.length} mapeamentos`}</Typography>
                                                                    ) : null}
                                                                    <Checkbox
                                                                        checked={(selectedCurrents[groupName] ?? []).includes(item.teleObjectId)}
                                                                        onChange={() => toggleChild(groupName, item.teleObjectId)}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    )
                                })
                            )}

                        </Grid>

                    </Grid>
                </ACard>
        </Drawer>
    )
}